import React, { useState, useCallback, useEffect, useRef } from 'react';
import { LogOut } from 'lucide-react';
import { useAuth } from '@empowernow/bff-auth-react';
import { BrandLogo } from '../../components/BrandLogo';

type SseStatus = 'connected' | 'stale' | 'disconnected';

interface OperatorHeaderProps {
  sseStatus?: SseStatus;
  tenantName?: string;
}

export function OperatorHeader({ sseStatus = 'connected', tenantName = 'EmpowerID' }: OperatorHeaderProps) {
  const { user } = useAuth();
  const [profileOpen, setProfileOpen] = useState(false);
  const [closing, setClosing] = useState(false);
  const sheetRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);

  const displayName = user?.name || user?.email || 'User';

  const initials = displayName
    .split(' ')
    .map((s: string) => s[0])
    .join('')
    .slice(0, 2)
    .toUpperCase();

  const closeSheet = useCallback(() => {
    setClosing(true);
    setTimeout(() => {
      setProfileOpen(false);
      setClosing(false);
      triggerRef.current?.focus();
    }, 250);
  }, []);

  const handleLogout = useCallback(() => {
    try {
      const form = document.createElement('form');
      form.method = 'POST';
      form.action = '/auth/logout?everywhere=true';
      document.body.appendChild(form);
      form.submit();
    } catch {
      window.location.assign('/auth/logout?everywhere=true');
    }
  }, []);

  // Focus trap + Escape key
  useEffect(() => {
    if (!profileOpen || closing) return;

    const sheet = sheetRef.current;
    if (!sheet) return;

    const focusable = sheet.querySelectorAll<HTMLElement>(
      'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])',
    );
    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    first?.focus();

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        closeSheet();
        return;
      }
      if (e.key === 'Tab') {
        if (focusable.length === 0) { e.preventDefault(); return; }
        if (e.shiftKey && document.activeElement === first) {
          e.preventDefault();
          last?.focus();
        } else if (!e.shiftKey && document.activeElement === last) {
          e.preventDefault();
          first?.focus();
        }
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [profileOpen, closing, closeSheet]);

  const statusLabels: Record<SseStatus, string> = {
    connected: 'Live',
    stale: 'Reconnecting',
    disconnected: 'Offline',
  };

  return (
    <>
      <header className="operator-header" data-testid="operator-header" role="banner">
        <div className="operator-header-left">
          <BrandLogo
            height={22}
            alt="EmpowerID"
            className="operator-header-logo"
            style={{ filter: 'var(--header-logo-filter, none)' } as React.CSSProperties}
          />
          <span className="operator-header-live" aria-label={`Connection status: ${statusLabels[sseStatus]}`}>
            <span className="operator-live-dot" data-status={sseStatus} aria-hidden="true" />
            {statusLabels[sseStatus]}
          </span>
        </div>
        <div className="operator-header-right">
          <span className="operator-header-tenant">{tenantName}</span>
          <button
            ref={triggerRef}
            className="operator-avatar"
            onClick={() => setProfileOpen(true)}
            aria-label="Open profile"
            aria-haspopup="dialog"
            data-testid="operator-avatar"
          >
            {initials}
          </button>
        </div>
      </header>

      {profileOpen && (
        <div
          ref={sheetRef}
          className="operator-profile-sheet"
          role="dialog"
          aria-modal="true"
          aria-label="Profile"
          aria-describedby="operator-profile-desc"
          data-testid="operator-profile-sheet"
        >
          <div
            className="operator-profile-backdrop"
            style={{ opacity: closing ? 0 : 1, transition: 'opacity 250ms ease' }}
            onClick={closeSheet}
            role="presentation"
          />
          <div
            className="operator-profile-content"
            style={{
              transform: closing ? 'translateY(100%)' : 'translateY(0)',
              transition: 'transform 250ms cubic-bezier(0.4, 0, 0.2, 1)',
              animation: closing ? undefined : 'operator-sheet-up 250ms cubic-bezier(0.4, 0, 0.2, 1) both',
            }}
          >
            <div className="operator-profile-handle" />
            <div className="operator-profile-name" id="operator-profile-desc">{displayName}</div>
            <div className="operator-profile-email">
              {user?.email || ''}
            </div>
            <button
              className="operator-profile-action operator-profile-action--danger"
              onClick={handleLogout}
              data-testid="operator-logout"
            >
              <LogOut size={16} strokeWidth={1.8} aria-hidden="true" />
              Sign out
            </button>
          </div>
        </div>
      )}
    </>
  );
}
